import type { ComponentType, ReactNode, SVGProps } from "react";

import type { RequestListItemStatusEnumKey } from "@synthesis/shared-services/external/application/types";
import { IconApplicationStatusExport, IconApplicationStatusImport } from "@synthesis/shared-icons";
import { NoticeBase, type NoticeBaseStatus } from "@synthesis/shared-ui";

import { useClientType, type ClientType } from "../../../shared/hooks/use-client-type";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import { getApplicationDetailProcessingState, type ApplicationProcessingState } from "../../applications-page/applications-page.lib";
import { APPLICATION_STATUS_TO_UI_STATUS, type ApplicationStatus, type ApplicationUiStatus } from "../../applications-page/applications-page.status";
import { useApplicationDetail } from "../hooks/use-application-detail";
import { useApplicationHistory } from "../hooks/use-application-history";

type StatusNoticeContent = {
  title: string;
  description?: ReactNode;
  noticeStatus: NoticeBaseStatus;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
};

const UI_STATUS_TO_NOTICE_STATUS: Record<ApplicationUiStatus, NoticeBaseStatus> = {
  NEUTRAL: "info",
  IN_QUEUE: "info",
  SUCCESS: "success",
  ERROR: "error",
  TO_REUPLOAD: "info",
};

const APPLICATION_STATUS_OPERATOR_NOTICE_INTERNAL: Partial<Record<ApplicationStatus, Omit<StatusNoticeContent, "noticeStatus">>> = {
  INTAKE_CHECK: {
    title: "Заявка импортирована во внутренний контур",
    description: "Ожидайте изменения статуса для передачи заявки во внешний контур заявителю.",
  },
  REVISION_REQUIRED: {
    title: "Заявка ожидает доработки заявителем",
    description: "Экспортируйте заявку и загрузите во внешний контур для доработки заявителем.",
  },
};

const APPLICATION_STATUS_OPERATOR_NOTICE_EXTERNAL: Partial<Record<ApplicationStatus, Omit<StatusNoticeContent, "noticeStatus">>> = {
  IN_REVIEW: {
    title: "Требуется выгрузка",
    description: "Экспортируйте заявку и загрузите во внутренний контур",
  },
  REVISION_REQUIRED: {
    title: "Заявка ожидает доработки заявителем",
    description: "Дождитесь доработки заявителем, экспортируйте заявку и загрузите во внутренний контур",
  },
  WAITING_PAYMENT: {
    title: "Заявка ожидает оплаты заявителем",
    description: "Экспортируйте заявку и загрузите во внутренний контур после успешной оплаты заявителем.",
  },
};

const APPLICATION_STATUS_APPLICANT_NOTICE_EXTERNAL: Partial<Record<RequestListItemStatusEnumKey, StatusNoticeContent>> = {
  REVISION_REQUIRED: {
    title: "Требуется доработка",
    description:
      "Ознакомьтесь с комментариями к заявке, внесите необходимые изменения и при необходимости приложите дополнительные документы. После внесения корректировок уведомите РЖД.",
    noticeStatus: "pending",
  },
  WAITING_PAYMENT: {
    title: "Ожидается оплата услуг",
    description: "Для продолжения рассмотрения заявки загрузите подтверждение оплаты во вкладке «Оплата» и направьте заявку в РЖД.",
    noticeStatus: "info",
  },
  RESOLVED_APPROVED: {
    title: "Выданы технические условия",
    description: (
      <>
        По результатам рассмотрения заявки принято решение о выдаче технических условий.
        <br />
        Ознакомьтесь с условиями и приложенными документами во вкладке «Результаты рассмотрений».
      </>
    ),
    noticeStatus: "success",
  },
  RESOLVED_REJECTED: {
    title: "Отказано в выдаче технических условий",
    description: (
      <>
        По результатам рассмотрения заявки принято решение об отказе в выдаче технических условий.
        <br />
        Ознакомьтесь с обоснованием во вкладке «Результаты рассмотрений».
      </>
    ),
    noticeStatus: "error",
  },
};

type SecretaryIntakeNoticeVariant = "first-intake" | "after-applicant-revision";

const SECRETARY_INTAKE_CHECK_NOTICE: Record<SecretaryIntakeNoticeVariant, StatusNoticeContent> = {
  "first-intake": {
    title: "Проверьте заявку",
    description:
      "Если есть замечания — нажмите «Отправить на доработку» и укажите их в открывшемся окне. Замечания сохранятся во вкладке «Результаты рассмотрения». Если замечаний нет — выберите адресата для дальнейшего рассмотрения.",
    noticeStatus: "info",
  },
  "after-applicant-revision": {
    title: "Заявка поступила после доработки заявителем",
    description:
      "Ознакомьтесь с внесёнными изменениями и проверьте, устранены ли ранее выявленные замечания. При необходимости повторно верните заявку на доработку или назначьте адресата для дальнейшего рассмотрения.",
    noticeStatus: "pending",
  },
};

const APPARATUS_NG_SENT_TO_NG_NOTICE: Record<SecretaryIntakeNoticeVariant, StatusNoticeContent> = {
  "first-intake": {
    title: "Запуск процесса рассмотрения",
    description: "Для начала рассмотрения заявки примите её в работу.",
    noticeStatus: "info",
  },
  "after-applicant-revision": {
    title: "Заявка поступила после доработки заявителем",
    description: (
      <>
        Примите заявку в работу для начала рассмотрения.
        <br />
        Проверьте внесённые изменения и устранение замечаний.
        <br />
        При необходимости верните заявку на доработку либо выберите действие: оформить отказ или направить в НТП.
      </>
    ),
    noticeStatus: "pending",
  },
};

const APPARATUS_NG_IN_NG_NOTICE: StatusNoticeContent = {
  title: "Заявка принята в работу",
  description:
    "Выберите дальнейшее действие по заявке: вернуть на доработку, оформить отказ или направить в НТП. При необходимости добавьте комментарий и приложите документы.",
  noticeStatus: "pending",
};

type NgApprovalNoticeVariant = "first-approval" | "after-ntp-rework";

const APPARATUS_NG_APPROVAL_NOTICE: Record<NgApprovalNoticeVariant, StatusNoticeContent> = {
  "first-approval": {
    title: "Итоговое решение",
    description: (
      <>
        Для согласования заявки:
        <br />
        1. Во вкладке «Оплата» выберите признак оплаты.
        <br />
        2. Если услуга платная — после получения счёта заполните сведения об оплате и дождитесь подтверждения. Кнопка «Согласовать» станет активна
        после выполнения всех условий.
        <br />
        3. Во вкладке «Решение и замечания» проверьте сформированное решение и примите решение: согласовать или направить на доработку.
      </>
    ),
    noticeStatus: "info",
  },
  "after-ntp-rework": {
    title: "Итоговое решение возвращено с доработки",
    description: (
      <>
        Проверьте внесённые изменения и устранение ранее указанных замечаний.
        <br />
        <br />
        Если замечания устранены:
        <br />• убедитесь, что условия оплаты выполнены (для платных услуг);
        <br />• согласуйте решение.
        <br />
        <br />
        Если требуется доработка — направьте решение на доработку повторно.
      </>
    ),
    noticeStatus: "pending",
  },
};

const APPARATUS_NG_NOTICE_STATUSES = ["SENT_TO_NG", "IN_NG", "NG_APPROVAL"] as const satisfies readonly ApplicationStatus[];

const isApparatusNgNoticeStatus = (status: ApplicationStatus | undefined): status is (typeof APPARATUS_NG_NOTICE_STATUSES)[number] =>
  status !== undefined && (APPARATUS_NG_NOTICE_STATUSES as readonly ApplicationStatus[]).includes(status);

const NTP_CHIEF_SENT_TO_NTP_NOTICE: StatusNoticeContent = {
  title: "Запуск процесса рассмотрения",
  description: "Для начала рассмотрения заявки примите её в работу.",
  noticeStatus: "info",
};

const NTP_CHIEF_IN_NTP_NOTICE: StatusNoticeContent = {
  title: "Назначьте ответственных",
  description: "Назначьте ответственного в НТП. При необходимости назначьте ответственных в регионе.",
  noticeStatus: "pending",
};

type NtpApprovalNoticeVariant = "first-approval" | "after-ntp-rework";

const NTP_CHIEF_APPROVAL_NOTICE: Record<NtpApprovalNoticeVariant, StatusNoticeContent> = {
  "first-approval": {
    title: "Проверка итогового решения",
    description:
      "Проверьте сформированное решение по заявке и примите решение: согласовать или направить на доработку. Итоговое решение доступно во вкладке «Результаты рассмотрений», где можно просмотреть документ и оставить замечания при необходимости.",
    noticeStatus: "info",
  },
  "after-ntp-rework": {
    title: "Итоговое решение возвращено с доработки",
    description: (
      <>
        Проверьте внесённые изменения и устранение ранее указанных замечаний.
        <br />
        <br />
        При необходимости повторно направьте решение на доработку либо согласуйте его.
      </>
    ),
    noticeStatus: "pending",
  },
};

const NTP_CHIEF_NOTICE_STATUSES = ["SENT_TO_NTP", "IN_NTP", "NTP_APPROVAL"] as const satisfies readonly ApplicationStatus[];

const isNtpChiefNoticeStatus = (status: ApplicationStatus | undefined): status is (typeof NTP_CHIEF_NOTICE_STATUSES)[number] =>
  status !== undefined && (NTP_CHIEF_NOTICE_STATUSES as readonly ApplicationStatus[]).includes(status);

const NTP_RESPONSIBLE_NOTICE_BY_STATUS = {
  NTP_RESP_ASSIGNED: {
    title: "Запуск процесса рассмотрения",
    description: "Для начала рассмотрения заявки примите её в работу.",
    noticeStatus: "info",
  },
  COMMISSION_PREP: {
    title: "Подготовьте проведение комиссии",
    description: (
      <>
        Сформируйте состав комиссии: назначьте ответственных в регионах и прикрепите необходимые документы во вкладке «Документы рассмотрения». После
        получения акта нажмите «Комиссия проведена».
      </>
    ),
    noticeStatus: "pending",
  },
  DECISION_FORMING: {
    title: "Подготовьте итоговое решение",
    description: (
      <>
        Загрузите части технических условий во вкладке «Документы рассмотрения».
        <br />
        На их основе сформируйте проект решения (ТУ или отказ).
        <br />
        Готовый проект загрузите во вкладке «Результаты рассмотрений» и отправьте на согласование.
      </>
    ),
    noticeStatus: "pending",
  },
  DECISION_REWORK: {
    title: "Решение возвращено на доработку",
    description:
      "Итоговое решение по заявке возвращено на доработку. Ознакомьтесь с замечаниями во вкладке «Результаты рассмотрений», внесите изменения в новую версию документа и повторно направьте решение на согласование.",
    noticeStatus: "pending",
  },
} as const satisfies Partial<Record<ApplicationStatus, StatusNoticeContent>>;

const NTP_RESPONSIBLE_NOTICE_STATUSES = [
  "NTP_RESP_ASSIGNED",
  "COMMISSION_PREP",
  "DECISION_FORMING",
  "DECISION_REWORK",
] as const satisfies readonly ApplicationStatus[];

const isNtpResponsibleNoticeStatus = (status: ApplicationStatus | undefined): status is (typeof NTP_RESPONSIBLE_NOTICE_STATUSES)[number] =>
  status !== undefined && (NTP_RESPONSIBLE_NOTICE_STATUSES as readonly ApplicationStatus[]).includes(status);

const getNtpResponsibleNotice = (status: (typeof NTP_RESPONSIBLE_NOTICE_STATUSES)[number]): StatusNoticeContent =>
  NTP_RESPONSIBLE_NOTICE_BY_STATUS[status];

const getOperatorProcessingNotice = (processingState: ApplicationProcessingState, clientType: ClientType): StatusNoticeContent => {
  if (processingState === "REQUIRES_EXPORT") {
    return {
      title: "Требуется выгрузка",
      description:
        clientType === "external" ? "Экспортируйте заявку и загрузите во внутренний контур" : "Экспортируйте заявку и загрузите во внешний контур",
      noticeStatus: "pending",
    };
  }

  if (processingState === "EXPORTED") {
    return {
      title: "Выгружено",
      description: "Данные заявки успешно выгружены.",
      noticeStatus: "info",
      icon: IconApplicationStatusExport,
    };
  }

  return {
    title: "Загружено",
    description: "Данные заявки успешно загружены и обработаны.",
    noticeStatus: "success",
    icon: IconApplicationStatusImport,
  };
};

const getOperatorStatusNotice = (status: ApplicationStatus | undefined, clientType: ClientType) => {
  if (!status) {
    return null;
  }

  const uiStatus = APPLICATION_STATUS_TO_UI_STATUS[status];

  if (!uiStatus) {
    return null;
  }

  const content =
    clientType === "external" ? APPLICATION_STATUS_OPERATOR_NOTICE_EXTERNAL[status] : APPLICATION_STATUS_OPERATOR_NOTICE_INTERNAL[status];

  if (!content) {
    return null;
  }

  return {
    noticeStatus: UI_STATUS_TO_NOTICE_STATUS[uiStatus],
    ...content,
  };
};

const getApplicantStatusNotice = (status: ApplicationStatus | undefined) => {
  if (!status) {
    return null;
  }

  const content = APPLICATION_STATUS_APPLICANT_NOTICE_EXTERNAL[status as RequestListItemStatusEnumKey];

  if (!content) {
    return null;
  }

  return content;
};

const getSecretaryIntakeCheckNotice = (variant: SecretaryIntakeNoticeVariant): StatusNoticeContent => SECRETARY_INTAKE_CHECK_NOTICE[variant];

const getApparatusNgNotice = (
  status: (typeof APPARATUS_NG_NOTICE_STATUSES)[number],
  isReturnedAfterApplicantRevision: boolean,
  isNgApprovalAfterNtpRework: boolean,
): StatusNoticeContent => {
  if (status === "SENT_TO_NG") {
    return APPARATUS_NG_SENT_TO_NG_NOTICE[isReturnedAfterApplicantRevision ? "after-applicant-revision" : "first-intake"];
  }

  if (status === "IN_NG") {
    return APPARATUS_NG_IN_NG_NOTICE;
  }

  return APPARATUS_NG_APPROVAL_NOTICE[isNgApprovalAfterNtpRework ? "after-ntp-rework" : "first-approval"];
};

const getNtpChiefNotice = (status: (typeof NTP_CHIEF_NOTICE_STATUSES)[number], isNtpApprovalAfterNtpRework: boolean): StatusNoticeContent => {
  if (status === "SENT_TO_NTP") {
    return NTP_CHIEF_SENT_TO_NTP_NOTICE;
  }

  if (status === "IN_NTP") {
    return NTP_CHIEF_IN_NTP_NOTICE;
  }

  return NTP_CHIEF_APPROVAL_NOTICE[isNtpApprovalAfterNtpRework ? "after-ntp-rework" : "first-approval"];
};

const requiresHistoryForNotice = (status: ApplicationStatus | undefined): boolean =>
  status === "INTAKE_CHECK" || status === "SENT_TO_NG" || status === "NG_APPROVAL" || status === "NTP_APPROVAL";

export const ApplicationStatusBanner = () => {
  const { application } = useApplicationDetail();
  const clientType = useClientType();
  const userRoles = useUserRoles();
  const {
    isReturnedAfterApplicantRevision,
    isNgApprovalAfterNtpRework,
    isNtpApprovalAfterNtpRework,
    isLoading: isHistoryLoading,
  } = useApplicationHistory(application?.requestId ?? "");

  if (!application) {
    return null;
  }

  const isInternalNgSecretary = clientType === "internal" && userRoles.isInternalNgSecretary;
  const isInternalApparatusNg = clientType === "internal" && userRoles.isInternalApparatusNg;
  const isInternalNtpChief = clientType === "internal" && userRoles.isInternalNtpChief;
  const isInternalNtpResponsible = clientType === "internal" && userRoles.isInternalNtpResponsible;
  const isExternalApplicant = clientType === "external" && userRoles.isExternalApplicant;
  const isOperator = (clientType === "external" && userRoles.isExternalOperator) || (clientType === "internal" && userRoles.isInternalOperator);

  if (!isInternalNgSecretary && !isInternalApparatusNg && !isInternalNtpChief && !isInternalNtpResponsible && !isExternalApplicant && !isOperator) {
    return null;
  }

  if (isInternalNgSecretary) {
    if (application.status !== "INTAKE_CHECK") {
      return null;
    }

    if (isHistoryLoading) {
      return null;
    }

    const notice = getSecretaryIntakeCheckNotice(isReturnedAfterApplicantRevision ? "after-applicant-revision" : "first-intake");

    return <NoticeBase status={notice.noticeStatus} title={notice.title} description={notice.description} />;
  }

  if (isInternalApparatusNg) {
    if (!isApparatusNgNoticeStatus(application.status)) {
      return null;
    }

    if (requiresHistoryForNotice(application.status) && isHistoryLoading) {
      return null;
    }

    const notice = getApparatusNgNotice(application.status, isReturnedAfterApplicantRevision, isNgApprovalAfterNtpRework);

    return <NoticeBase status={notice.noticeStatus} title={notice.title} description={notice.description} />;
  }

  if (isInternalNtpChief) {
    if (!isNtpChiefNoticeStatus(application.status)) {
      return null;
    }

    if (requiresHistoryForNotice(application.status) && isHistoryLoading) {
      return null;
    }

    const notice = getNtpChiefNotice(application.status, isNtpApprovalAfterNtpRework);

    return <NoticeBase status={notice.noticeStatus} title={notice.title} description={notice.description} />;
  }

  if (isInternalNtpResponsible) {
    if (!isNtpResponsibleNoticeStatus(application.status)) {
      return null;
    }

    const notice = getNtpResponsibleNotice(application.status);

    return <NoticeBase status={notice.noticeStatus} title={notice.title} description={notice.description} />;
  }

  if (isExternalApplicant) {
    const notice = application.status && getApplicantStatusNotice(application.status);

    if (!notice) {
      return null;
    }

    return <NoticeBase status={notice.noticeStatus} title={notice.title} description={notice.description} />;
  }

  const processingState = getApplicationDetailProcessingState(application);
  const processingNotice = processingState ? getOperatorProcessingNotice(processingState, clientType) : null;
  const statusNotice = application.status && getOperatorStatusNotice(application.status, clientType);
  const notice = processingNotice ?? statusNotice;

  if (!notice) {
    return null;
  }

  if (!notice.title && !notice.description) {
    return null;
  }

  return <NoticeBase status={notice.noticeStatus} title={notice.title} description={notice.description} icon={notice.icon} />;
};
